import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
  Box,
} from "@mui/material";
import { Add, Delete, Edit, Receipt } from "@mui/icons-material";
import ChairIcon from "@mui/icons-material/Chair";
import toast from "react-hot-toast";
import PendingBookingsModal from "./PendingBookingsModal";
import BookingModal from "./BookingModal";

const API = process.env.REACT_APP_API_URL;

// Ab yeh component sirf Library ke seats handle karega
const RoomLibraryManager = () => {
  const [data, setData] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    title: "",
    message: "",
    onConfirm: null,
  });
  const [form, setForm] = useState({
    number: "",
    description: "",
  });
  const [selectedSeat, setSelectedSeat] = useState(null);
  const [isGeneratingInvoices, setIsGeneratingInvoices] = useState(false);

  // Get token dynamically
  const getConfig = () => {
    const token = localStorage.getItem("token");
    return {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    };
  };

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        console.log("No token available, skipping fetch");
        return;
      }

      const endpoint = "library/seats/live-map/";
      const res = await axios.get(`${API}${endpoint}`, getConfig());
      setData(res.data);
    } catch (err) {
      toast.error("Failed to fetch data");
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpen = (item = null) => {
    if (item) {
      setEditItem(item);
      setForm({
        number: item.seat_number,
        description: item.description || "",
      });
    } else {
      setEditItem(null);
      setForm({
        number: "",
        description: "",
      });
    }
    setOpenModal(true);
  };

  const handleClose = () => {
    setOpenModal(false);
    setEditItem(null);
    setForm({ number: "", description: "" });
  };

  const showConfirmModal = (title, message, onConfirm) => {
    setConfirmModal({
      open: true,
      title,
      message,
      onConfirm,
    });
  };

  const closeConfirmModal = () => {
    setConfirmModal({
      open: false,
      title: "",
      message: "",
      onConfirm: null,
    });
  };

  const handleConfirm = async () => {
    if (confirmModal.onConfirm) {
      await confirmModal.onConfirm();
    }
    closeConfirmModal();
  };

  const handleSubmit = async () => {
    // Duplicate check (global for seats)
    const duplicate = data.find(
      (seat) =>
        seat.seat_number === form.number &&
        (!editItem || seat.id !== editItem.id)
    );
    if (duplicate) {
      toast.error("A seat with this number already exists.");
      return;
    }

    const payload = {
      seat_number: form.number,
      description: form.description,
    };

    try {
      if (editItem) {
        const endpoint = `library/seats/${editItem.id}/`;
        await axios.put(`${API}${endpoint}`, payload, getConfig());
        toast.success("Updated successfully");
      } else {
        const endpoint = "library/seats/";
        await axios.post(`${API}${endpoint}`, payload, getConfig());
        toast.success("Created successfully");
      }
      await fetchData();
      handleClose();
    } catch (err) {
      toast.error("Failed to submit");
    }
  };

  const handleDelete = async (id) => {
    showConfirmModal(
      "Delete Seat",
      "Are you sure you want to delete this seat? This action cannot be undone.",
      async () => {
        try {
          const endpoint = `library/seats/${id}/`;
          await axios.delete(`${API}${endpoint}`, getConfig());
          toast.success("Deleted successfully");
          await fetchData();
        } catch (err) {
          toast.error(err.response?.data?.error || "Failed to delete");
        }
      }
    );
  };

  const handleGenerateInvoices = async () => {
    setIsGeneratingInvoices(true);
    try {
      const endpoint = "library/admin/generate-central-invoices/";
      const response = await axios.post(`${API}${endpoint}`, {}, getConfig());

      if (response.data.message) {
        toast.success(response.data.message);
      } else {
        toast.success("Invoices generated successfully");
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.error ||
        err.response?.data?.message ||
        "Failed to generate invoices due to all invoices generated";
      toast.error(errorMessage);
    } finally {
      setIsGeneratingInvoices(false);
    }
  };

  // ====== Library seats layout (as-is) ======
  const renderLibrarySeats = () => {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4">
        {data.map((item) => (
          <div
            key={item.id}
            className={`relative border p-3 md:p-4 rounded-md shadow-sm ${
              item.is_paid ? "bg-[#ffeaea]" : "bg-white"
            }`}
          >
            <div className="flex justify-between items-start mb-2">
              <div
                onClick={() =>
                  (item.status === "pending" || item.status === "booked") &&
                  setSelectedSeat(item)
                }
                className="flex items-center gap-1 cursor-pointer relative"
              >
                <span className="relative flex items-center">
                  <ChairIcon
                    fontSize="large"
                    style={{
                      color:
                        item.status === "available"
                          ? "#4CAF50"
                          : item.status === "booked"
                          ? "#F44336"
                          : item.status === "pending"
                          ? "#FF9800"
                          : "#9CA3AF",
                    }}
                  />
                  {item.is_paid && (
                    <svg
                      width="70"
                      height="32"
                      viewBox="0 0 70 32"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="absolute -top-4 -left-6 rotate-[-15deg] z-10"
                      style={{ pointerEvents: "none" }}
                    >
                      <rect
                        x="0"
                        y="0"
                        width="70"
                        height="32"
                        rx="8"
                        fill="#fff"
                        stroke="#F44336"
                        strokeWidth="3"
                      />
                      <text
                        x="35"
                        y="22"
                        textAnchor="middle"
                        fill="#F44336"
                        fontSize="20"
                        fontWeight="bold"
                        fontFamily="monospace"
                        letterSpacing="2"
                        style={{ textTransform: "uppercase" }}
                      >
                        PAID
                      </text>
                    </svg>
                  )}
                  <span className="font-semibold text-sm md:text-lg ml-2">
                    {item.seat_number}
                  </span>
                  {item.pending_count > 1 && (
                    <span className="absolute -top-2 -right-3 bg-[#FF9800] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold shadow-lg animate-bounce z-10">
                      {item.pending_count}
                    </span>
                  )}
                </span>
              </div>
              <div className="flex gap-1">
                <IconButton onClick={() => handleOpen(item)} size="small">
                  <Edit fontSize="small" />
                </IconButton>
                <IconButton onClick={() => handleDelete(item.id)} size="small">
                  <Delete fontSize="small" />
                </IconButton>
              </div>
            </div>
            <p className="text-xs md:text-sm text-gray-600 mb-2 line-clamp-2">
              {item.description}
            </p>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="p-3 md:p-4">
      {/* Status Legend + Invoice Button */}
      <div className="flex justify-between flex-col sm:flex-row gap-4 md:gap-0 items-center text-sm font-medium text-gray-700 mb-4">
        <div className="flex gap-6">
          <div className="flex items-center gap-2">
            <div
              className="w-4 h-4 rounded"
              style={{ backgroundColor: "#4CAF50" }}
            ></div>
            Available
          </div>
          <div className="flex items-center gap-2">
            <div
              className="w-4 h-4 rounded"
              style={{ backgroundColor: "#FF9800" }}
            ></div>
            Pending
          </div>
          <div className="flex items-center gap-2">
            <div
              className="w-4 h-4 rounded"
              style={{ backgroundColor: "#F44336" }}
            ></div>
            Booked
          </div>
        </div>
        <Button
          startIcon={<Receipt />}
          variant="contained"
          onClick={handleGenerateInvoices}
          disabled={isGeneratingInvoices}
          className="bg-green-600 hover:bg-green-700 text-white text-sm"
          size="small"
        >
          {isGeneratingInvoices ? "Generating..." : "Generate Library Invoices"}
        </Button>
      </div>

      {/* Header + Add Seat Button */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4 md:mb-6">
        <div className="flex flex-wrap gap-2 w-full sm:w-auto">
          <Button
            startIcon={<Add />}
            variant="outlined"
            onClick={() => handleOpen()}
            className="border-blue-600 text-blue-600 hover:bg-blue-50 text-sm md:text-base"
            size="small"
          >
            Add Seat
          </Button>
        </div>
      </div>

      {/* Content */}
      {data.length === 0 ? (
        <Box className="text-center py-6 md:py-8">
          <Typography
            variant="h6"
            className="text-gray-500 text-base md:text-xl"
          >
            No seats available.
          </Typography>
        </Box>
      ) : (
        renderLibrarySeats()
      )}

      {/* Create / Update Seat Modal */}
      <Dialog open={openModal} onClose={handleClose} fullWidth>
        <DialogTitle>
          {editItem ? "Update Seat" : "Create Seat"}
        </DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            paddingTop: "6px !important",
          }}
        >
          <TextField
            label="Seat Number/Name"
            value={form.number}
            onChange={(e) => setForm({ ...form, number: e.target.value })}
            required
            disabled={editItem && editItem.status !== "available"}
            helperText={
              editItem && editItem.status !== "available"
                ? "Seat number can only be changed when status is Available"
                : ""
            }
          />
          <TextField
            label="Description"
            value={form.description}
            onChange={(e) =>
              setForm({ ...form, description: e.target.value })
            }
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editItem ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Modal */}
      <Dialog
        open={confirmModal.open}
        onClose={closeConfirmModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          className: "rounded-lg",
        }}
      >
        <DialogTitle className="flex items-center gap-2 text-red-600">
          <Delete color="error" />
          {confirmModal.title}
        </DialogTitle>
        <DialogContent
          sx={{
            paddingTop: "6px !important",
            paddingBottom: "16px !important",
          }}
        >
          <Typography variant="body1" className="text-gray-700">
            {confirmModal.message}
          </Typography>
        </DialogContent>
        <DialogActions className="p-4 gap-2">
          <Button
            onClick={closeConfirmModal}
            variant="outlined"
            className="border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            color="error"
            className="bg-red-600 hover:bg-red-700"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Library seat modals */}
      {selectedSeat && selectedSeat.status === "pending" && (
        <PendingBookingsModal
          bedSeatId={selectedSeat.id}
          type="library"
          onClose={() => setSelectedSeat(null)}
        />
      )}
      {selectedSeat && selectedSeat.status === "booked" && (
        <BookingModal
          bookingId={selectedSeat.booking_id}
          type="library"
          onClose={() => setSelectedSeat(null)}
          onAction={() => {
            fetchData();
          }}
        />
      )}
    </div>
  );
};

export default RoomLibraryManager;
